import { useEffect, useRef, useState } from "react";
import { UAParser } from "ua-parser-js";
import { useLocation, useNavigate } from "react-router-dom";
import { showToast } from "../toastService";
import { AUTH_BASE_URL, VERSION, APP_VERSION, apiClientHeaders } from "../utils/config";
import "../styles/Otp.css";

export default function Otp() {
  const location = useLocation();
  const phone = location.state?.phone || "";
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const inputsRef = useRef([]);
  const [timeLeft, setTimeLeft] = useState(90);
  const [formLoading, setFormLoading] = useState(false);
  const [retryOTPDisabled, setRetryOTPDisabled] = useState(false);
  const navigate = useNavigate();

  const parser = new UAParser();
  const result = parser.getResult();

  // ✅ Redirect if phone is invalid
  useEffect(() => {
    if (!phone || phone.length !== 10) {
      navigate(-1); // go back
    }
  }, [phone, navigate]);

  // Countdown timer
  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  // Handle input change
  const handleChange = (value, index) => {
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value) {
        if (index < 5) {
          // move to next input
          inputsRef.current[index + 1].focus();
        } else {
          // ✅ last digit entered → close keyboard
          inputsRef.current[index].blur();
        }
      }
    }
  };

  // Handle backspace to go to previous input
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  // Resend OTP
  const handleResend = async () => {
    if (timeLeft > 0) return;
    setRetryOTPDisabled(true);
    try {
      const response = await fetch(`${AUTH_BASE_URL}/${VERSION}/user-web/login`, {
        method: "POST",
        body: JSON.stringify({ phone: "91"+phone, app_detect: "web_detect" }),
        headers: apiClientHeaders({ "Content-Type": "application/json" }),
      });

      const data = await response.json();
      setRetryOTPDisabled(false);
      if (data.response_code === 200) {
        showToast("The OTP has been sent successfully!", "success");
        setOtp(new Array(6).fill(""));
        setTimeLeft(90);
      } else {
        showToast(data.message || "Error sending OTP", "error");
      }
    } catch (error) {
      setRetryOTPDisabled(false);
      console.log(error)
      showToast("Failed to send OTP. Try again.", "error");
    }
  };

  const formattedTime = `${Math.floor(timeLeft / 60)}:${String(
    timeLeft % 60
  ).padStart(2, "0")}`;

  const handleVerify = async () => {
    setFormLoading(true);
    setRetryOTPDisabled(true);
    const enteredOtp = otp.join("");
    if (enteredOtp.length === 6) {
      try {
        const response = await fetch(`${AUTH_BASE_URL}/${VERSION}/user-web/validate/otp`, {
          method: "POST",
          body: JSON.stringify({ phone: "91"+phone, otp: enteredOtp,
            device: {
              name: result.device.model || result.browser.name || "Unknown",
              os: result.os.name
                  ? result.os.name.toLowerCase().includes("ios")
                    ? "ios"
                    : "android"
                  : "android",
              os_version: result.os.version || "unknown",
              app_version: APP_VERSION,
              fcm: "WEBNOFCMDETECTED",
            }
           }),
          headers: apiClientHeaders({ "Content-Type": "application/json" }),
        });

        const data = await response.json();
        setFormLoading(false);
        setRetryOTPDisabled(false);
        if (data.response_code === 200) {
          localStorage.setItem("accessToken", data.response.accessToken);
          localStorage.setItem("refreshToken", data.response.refreshToken);
          if(data.response.user && data.response.user.name ){
            localStorage.setItem("username", data.response.user.name)
          };
          localStorage.setItem("isAuthenticated", "true");
          showToast("Logged in successfully!", "success");
          const redirectPath = sessionStorage.getItem("redirectPath") || "/dashboard";
          sessionStorage.removeItem("redirectPath");
          navigate(redirectPath);
        } else {
          showToast(data.message || "Error logging in", "error");
        }
      } catch (error) {
        setFormLoading(false);
        setRetryOTPDisabled(false);
        console.log(error)
        showToast("Failed to log in. Try again.", "error");
      }
    }
  };

  // 1️⃣ Add a derived boolean
  const isOtpComplete = otp.every((digit) => digit !== "");

  return (
    <div className="otp-wrapper">
      <button className="back-btn" onClick={() => window.history.back()}>
        <img src="/assets/images/arrow_back_ios.png" alt="Go Back"></img>
      </button>
      <h2 className="otp-title">Verify OTP</h2>
      <p className="otp-subtitle">We’ve sent an OTP to +91 {phone}</p>

      <div className="otp-inputs">
        {otp.map((digit, index) => (
          <input
            key={index}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            ref={(el) => (inputsRef.current[index] = el)}
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className="otp-box"
          />
        ))}
      </div>

      <div className="resend">
        {timeLeft > 0 ? (
          <span>Didn’t receive any code? Retry in {formattedTime}</span>
        ) : (
          <>
            <span>Didn’t receive any code? </span>
            <button className="resend-btn" disabled={retryOTPDisabled} onClick={handleResend}>
              Resend OTP
            </button>
          </>
        )}
      </div>

      <button
        className="verify-btn"
        onClick={handleVerify}
        disabled={!isOtpComplete || formLoading}
      >
        {formLoading ? <span className="loader"></span> : "Continue"}
      </button>
    </div>
  );
}
